import { Briefcase, Handshake, MessageCircle, Lightbulb } from "lucide-react";
import type { ServiceType } from "./MultiStepFormContext";

// Question Types
export type QuestionType = "single" | "multi" | "text" | "textarea";

export interface QuestionOption {
  value: string;
  label: string;
  description?: string;
}

export interface QuestionConfig {
  id: string;
  type: QuestionType;
  label: string;
  description?: string;
  placeholder?: string;
  required: boolean;
  options?: QuestionOption[];
  showFor?: ServiceType[];
  maxSelections?: number;
}

// Service Path Definitions
export interface ServicePath {
  id: ServiceType;
  title: string;
  description: string;
  icon: typeof Briefcase;
}

export const SERVICE_PATHS: ServicePath[] = [
  {
    id: "project",
    title: "New Project",
    description: "Start a new web, branding, or marketing project",
    icon: Briefcase,
  },
  {
    id: "partnership",
    title: "Partnership",
    description: "Explore ongoing collaboration or retainer",
    icon: Handshake,
  },
  {
    id: "consultation",
    title: "Consultation",
    description: "Get expert advice on strategy or growth",
    icon: Lightbulb,
  },
  {
    id: "general",
    title: "General Inquiry",
    description: "Ask a question or say hello",
    icon: MessageCircle,
  },
];

// Questions for each service path
export const QUESTIONS: QuestionConfig[] = [
  // ========== PROJECT QUESTIONS ==========
  {
    id: "project_type",
    type: "single",
    label: "What type of project are you looking for?",
    required: true,
    showFor: ["project"],
    options: [
      { value: "website", label: "Website Design & Development", description: "New site or redesign" },
      { value: "branding", label: "Branding & Identity", description: "Logo, colors, brand guide" },
      { value: "marketing", label: "Marketing Campaign", description: "Ads, content, promotion" },
      { value: "content", label: "Content Production", description: "Photo, video, graphics" },
      { value: "product", label: "Product/UX Design", description: "App design, prototypes" },
      { value: "other", label: "Other", description: "Something else" },
    ],
  },
  {
    id: "project_deliverables",
    type: "multi",
    label: "What deliverables do you need?",
    description: "Select all that apply",
    required: false,
    showFor: ["project"],
    options: [
      { value: "logo", label: "Logo & Brand Guide" },
      { value: "website", label: "Website" },
      { value: "social", label: "Social Media Assets" },
      { value: "marketing", label: "Marketing Materials" },
      { value: "photo_video", label: "Photography/Video" },
      { value: "copywriting", label: "Copywriting" },
    ],
  },
  {
    id: "project_timeline",
    type: "single",
    label: "When do you need this completed?",
    required: true,
    showFor: ["project"],
    options: [
      { value: "asap", label: "ASAP (within 2 weeks)" },
      { value: "month", label: "This month" },
      { value: "quarter", label: "Next 1-3 months" },
      { value: "flexible", label: "Flexible" },
    ],
  },
  {
    id: "project_budget",
    type: "single",
    label: "What's your budget range?",
    required: true,
    showFor: ["project"],
    options: [
      { value: "under_5k", label: "Under $5K" },
      { value: "5k_15k", label: "$5K - $15K" },
      { value: "15k_30k", label: "$15K - $30K" },
      { value: "30k_plus", label: "$30K+" },
    ],
  },
  {
    id: "project_description",
    type: "textarea",
    label: "Tell us about your project",
    placeholder: "What are you building? What's the goal? Any important details...",
    required: true,
    showFor: ["project"],
  },

  // ========== PARTNERSHIP QUESTIONS ==========
  {
    id: "partnership_type",
    type: "single",
    label: "What type of partnership are you interested in?",
    required: true,
    showFor: ["partnership"],
    options: [
      { value: "retainer", label: "Monthly Retainer", description: "Ongoing dedicated hours" },
      { value: "revenue_share", label: "Revenue Share", description: "Performance-based partnership" },
      { value: "white_label", label: "White Label Services", description: "Work under your brand" },
      { value: "alliance", label: "Strategic Alliance", description: "Joint ventures & referrals" },
      { value: "other", label: "Other", description: "Something different" },
    ],
  },
  {
    id: "partnership_areas",
    type: "multi",
    label: "What areas would you like support with?",
    description: "Select all that apply",
    required: true,
    showFor: ["partnership"],
    options: [
      { value: "creative", label: "Creative Direction" },
      { value: "content", label: "Content Production" },
      { value: "marketing", label: "Marketing Strategy" },
      { value: "development", label: "Web Development" },
      { value: "brand", label: "Brand Management" },
      { value: "growth", label: "Growth & Analytics" },
    ],
  },
  {
    id: "partnership_budget",
    type: "single",
    label: "What's your monthly budget range?",
    required: true,
    showFor: ["partnership"],
    options: [
      { value: "2k_5k", label: "$2K - $5K/month" },
      { value: "5k_10k", label: "$5K - $10K/month" },
      { value: "10k_20k", label: "$10K - $20K/month" },
      { value: "20k_plus", label: "$20K+/month" },
    ],
  },
  {
    id: "partnership_goals",
    type: "textarea",
    label: "Tell us about your goals",
    placeholder: "What are you trying to achieve? What challenges are you facing?",
    required: true,
    showFor: ["partnership"],
  },

  // ========== CONSULTATION QUESTIONS ==========
  {
    id: "consultation_topics",
    type: "multi",
    label: "What do you need help with?",
    description: "Select all that apply",
    required: true,
    showFor: ["consultation"],
    options: [
      { value: "brand_strategy", label: "Brand Strategy" },
      { value: "marketing_audit", label: "Marketing Audit" },
      { value: "growth_strategy", label: "Growth Strategy" },
      { value: "tech_architecture", label: "Technical Architecture" },
      { value: "product_strategy", label: "Product Strategy" },
      { value: "team_training", label: "Team Training" },
    ],
  },
  {
    id: "consultation_format",
    type: "single",
    label: "What format works best for you?",
    required: true,
    showFor: ["consultation"],
    options: [
      { value: "one_time", label: "One-time session", description: "Single deep-dive session" },
      { value: "ongoing", label: "Ongoing advisory", description: "Regular check-ins" },
      { value: "workshop", label: "Workshop", description: "Team training session" },
    ],
  },
  {
    id: "consultation_challenge",
    type: "textarea",
    label: "What's your current challenge?",
    placeholder: "Describe the problem you're trying to solve...",
    required: true,
    showFor: ["consultation"],
  },

  // ========== GENERAL INQUIRY ==========
  {
    id: "message",
    type: "textarea",
    label: "How can we help?",
    placeholder: "Write your message here...",
    required: true,
    showFor: ["general"],
  },
];

// Referral source options
export const REFERRAL_SOURCES: QuestionOption[] = [
  { value: "google", label: "Google Search" },
  { value: "social", label: "Social Media" },
  { value: "referral", label: "Friend/Colleague" },
  { value: "portfolio", label: "Saw Your Work" },
  { value: "event", label: "Event/Conference" },
  { value: "other", label: "Other" },
];

// Helper functions
export function getQuestionsForService(serviceType: ServiceType): QuestionConfig[] {
  return QUESTIONS.filter(
    (q) => !q.showFor || q.showFor.includes(serviceType)
  );
}

export function isQuestionAnswered(
  question: QuestionConfig,
  answer: unknown
): boolean {
  if (!question.required) return true;

  if (answer === undefined || answer === null) return false;
  if (typeof answer === "string" && answer.trim() === "") return false;
  if (Array.isArray(answer) && answer.length === 0) return false;

  return true;
}

export function areAllRequiredAnswered(
  serviceType: ServiceType,
  answers: Record<string, unknown>
): boolean {
  const questions = getQuestionsForService(serviceType);
  return questions
    .filter((q) => q.required)
    .every((q) => isQuestionAnswered(q, answers[q.id]));
}
